// Deployed with GitHub Auto-Deploy enabled - Full 7-tab interface
import React, { useEffect, useState } from 'react';
import { 
  Box, 
  Container, 
  Heading, 
  Text, 
  VStack, 
  SimpleGrid, 
  Stat, 
  StatLabel, 
  StatNumber, 
  StatHelpText, 
  Alert, 
  AlertIcon, 
  AlertTitle,
  AlertDescription,
  Spinner,
  Card,
  CardHeader,
  CardBody,
  Badge,
  HStack,
  Divider,
  Progress,
  Icon,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Button,
  Flex
} from '@chakra-ui/react';
import { apiClient } from './services/apiClient';
import PriceChart from './components/charts/PriceChart';
import AnalyticsDashboard from './components/charts/AnalyticsDashboard';
import AdvancedFilter from './components/filters/AdvancedFilter';
import PortfolioIntegration from './components/portfolio/PortfolioIntegration';
import AlphaQuantBotPanel from './components/bot/AlphaQuantBotPanel';
import RWAPlaybook from './components/RWAPlaybook';

interface ApiData {
  message?: string;
  timestamp?: string;
  status?: string;
}

interface CryptoAsset {
  id: string;
  symbol: string;
  name: string;
  alphaScore: number;
  confidence: string;
  marketCap: number;
  price: number;
  volume24h: number;
  priceChange24h: number;
  stage: string;
  category: string;
  riskLevel: 'LOW' | 'MEDIUM' | 'HIGH';
  discovered: string;
  description: string;
  socialScore: number;
  devActivity: number;
  communitySize: number;
  alerts: string[];
  sector: string;
  change24h: number;
}

interface WalletData {
  address: string;
  totalValue: number;
  totalChange24h: number;
  assets: Array<{
    symbol: string;
    name: string;
    balance: number;
    value: number;
    price: number;
    change24h: number;
  }>;
}

function App() {
  const [apiStatus, setApiStatus] = useState<ApiData | null>(null);
  const [cryptoAssets, setCryptoAssets] = useState<CryptoAsset[]>([]);
  const [filteredAssets, setFilteredAssets] = useState<CryptoAsset[]>([]);
  const [alerts, setAlerts] = useState<any[]>([]);
  const [projects, setProjects] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdate, setLastUpdate] = useState<string>('');
  const [walletData, setWalletData] = useState<WalletData | null>(null);
  const [selectedAsset, setSelectedAsset] = useState<string>('');

  const fetchData = async () => {
    try {
      setError(null);
      
      // Fetch API status
      try {
        const statusResponse = await apiClient.checkHealth();
        setApiStatus(statusResponse);
      } catch (err) {
        console.warn('API health check failed:', err);
        setApiStatus({ message: 'API status unavailable', status: 'warning' });
      }
      
      // Fetch crypto assets
      try {
        const assetsResponse = await apiClient.getDiscoveries();
        const enhancedAssets = assetsResponse.map((asset: any) => ({
          ...asset,
          sector: asset.category || 'DeFi',
          change24h: asset.priceChange24h || 0,
          riskLevel: asset.riskLevel || 'MEDIUM'
        }));
        setCryptoAssets(enhancedAssets);
        setFilteredAssets(enhancedAssets);
      } catch (err) {
        console.warn('Failed to fetch crypto assets:', err);
        setCryptoAssets([]);
        setFilteredAssets([]);
      }
      
      // Fetch alerts
      try {
        const alertsResponse = await apiClient.getAlerts();
        setAlerts(alertsResponse);
      } catch (err) {
        console.warn('Failed to fetch alerts:', err);
        setAlerts([]);
      }
      
      // Fetch projects
      try {
        const projectsResponse = await apiClient.getProjects();
        setProjects(projectsResponse);
      } catch (err) {
        console.warn('Failed to fetch projects:', err);
        setProjects([]);
      }
      
      setLastUpdate(new Date().toLocaleString());
    } catch (err) {
      console.error('Unexpected error:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    
    // Set up auto-refresh every 30 seconds
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, []);

  const handleWalletConnected = (wallet: WalletData) => {
    setWalletData(wallet);
  };

  const handleFilteredAssets = (assets: CryptoAsset[]) => {
    setFilteredAssets(assets);
  };

  if (loading) {
    return (
      <Box
        height="100vh"
        display="flex"
        alignItems="center"
        justifyContent="center"
        bg="gray.50"
      >
        <VStack spacing={4}>
          <Spinner size="xl" color="blue.500" thickness="4px" />
          <Text color="gray.600">Loading Alpha Playbook...</Text>
        </VStack>
      </Box>
    );
  }

  return (
    <Box bg="gray.50" minHeight="100vh">
      <Container maxW="container.xl" py={8}>
        <VStack spacing={8} align="stretch">
          {/* Header */}
          <Card>
            <CardBody>
              <HStack justify="space-between" align="center" wrap="wrap">
                <VStack align="start" spacing={1}>
                  <HStack>
                    <Text fontSize="2xl" fontWeight="bold" color="blue.600">
                      🚀
                    </Text>
                    <Heading size="lg" color="gray.800">
                      🚀 Alpha Playbook Dashboard - UPDATED VERSION WITH ALPHA BOT!
                    </Heading>
                  </HStack>
                  <Text color="gray.600">Crypto opportunity discovery and analysis platform</Text>
                  <HStack spacing={2}>
                    <Icon viewBox="0 0 8 8" color="green.400">
                      <circle cx="4" cy="4" r="3" fill="currentColor" />
                    </Icon>
                    <Text fontSize="sm" color="gray.600">
                      {apiStatus?.message || 'Alpha Playbook API is running'}
                    </Text>
                  </HStack>
                </VStack>

                <VStack align="end" spacing={1}>
                  {apiStatus && (
                    <Alert status="success" size="sm" borderRadius="md" maxW="400px">
                      <AlertIcon />
                      <Box>
                        <AlertTitle fontSize="sm">API Status: OK</AlertTitle>
                        <AlertDescription fontSize="xs">
                          Environment: production | Last update {lastUpdate}
                        </AlertDescription>
                      </Box>
                    </Alert>
                  )}
                </VStack>
              </HStack>
            </CardBody>
          </Card>

          {error && (
            <Alert status="error">
              <AlertIcon />
              <AlertTitle>Error!</AlertTitle>
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {/* Enhanced Tabbed Interface */}
          <Tabs colorScheme="blue" variant="enclosed">
            <TabList>
              <Tab>🎯 Discovery</Tab>
              <Tab>📊 Analytics</Tab>
              <Tab>💼 Portfolio</Tab>
              <Tab>💎 RWA Playbook - LIVE NOW</Tab>
              <Tab>⚠️ Alerts</Tab>
              <Tab>🚀 Projects</Tab>
              <Tab>🤖 ALPHA BOT HERE!!!</Tab>
            </TabList>

            <TabPanels>
              {/* Discovery Tab */}
              <TabPanel p={0} pt={6}>
                <VStack spacing={6} align="stretch">
                  {/* Quick Stats */}
                  <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4}>
                    <Stat bg="white" p={4} borderRadius="lg" shadow="sm" border="1px solid" borderColor="gray.200">
                      <StatLabel>Crypto Assets Tracked</StatLabel>
                      <StatNumber>{filteredAssets.length}</StatNumber>
                      <StatHelpText>of {cryptoAssets.length} total</StatHelpText>
                    </Stat>
                    <Stat bg="white" p={4} borderRadius="lg" shadow="sm" border="1px solid" borderColor="gray.200">
                      <StatLabel>Active Projects</StatLabel>
                      <StatNumber>{projects.filter(p => p.status === 'active').length}</StatNumber>
                      <StatHelpText>2 new projects</StatHelpText>
                    </Stat>
                    <Stat bg="white" p={4} borderRadius="lg" shadow="sm" border="1px solid" borderColor="gray.200">
                      <StatLabel>Average Alpha Score</StatLabel>
                      <StatNumber>
                        {filteredAssets.length > 0 
                          ? (filteredAssets.reduce((sum, asset) => sum + asset.alphaScore, 0) / filteredAssets.length).toFixed(1)
                          : 0}%
                      </StatNumber>
                      <StatHelpText>High confidence</StatHelpText>
                    </Stat>
                    <Stat bg="white" p={4} borderRadius="lg" shadow="sm" border="1px solid" borderColor="gray.200">
                      <StatLabel>Active Alerts</StatLabel>
                      <StatNumber>{alerts.length}</StatNumber>
                      <StatHelpText>7 high priority</StatHelpText>
                    </Stat>
                  </SimpleGrid>

                  {/* Advanced Filtering */}
                  <AdvancedFilter
                    assets={cryptoAssets}
                    onFilteredAssets={handleFilteredAssets}
                  />

                  {/* Featured Asset Chart */}
                  {selectedAsset && (
                    <PriceChart
                      symbol={selectedAsset}
                      height={400}
                    />
                  )}

                  {/* Assets Grid */}
                  <Box>
                    <Heading size="md" mb={4} color="gray.800">🔍 Latest Crypto Discoveries</Heading>
                    <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4}>
                      {filteredAssets.slice(0, 6).map((asset) => (
                        <Card 
                          key={asset.id} 
                          cursor="pointer"
                          _hover={{ transform: 'translateY(-2px)', shadow: 'lg' }}
                          transition="all 0.2s"
                          onClick={() => setSelectedAsset(asset.symbol)}
                        >
                          <CardHeader pb={2}>
                            <HStack justify="space-between">
                              <VStack align="start" spacing={1}>
                                <Text fontWeight="bold" fontSize="lg">{asset.symbol}</Text>
                                <Text color="gray.600" fontSize="sm">{asset.name}</Text>
                              </VStack>
                              <Badge
                                colorScheme={
                                  asset.alphaScore >= 80 ? 'green' : 
                                  asset.alphaScore >= 60 ? 'yellow' : 'red'
                                }
                                fontSize="lg"
                                px={3}
                                py={1}
                              >
                                {asset.alphaScore}%
                              </Badge>
                            </HStack>
                          </CardHeader>
                          <CardBody pt={0}>
                            <VStack align="stretch" spacing={3}>
                              <HStack justify="space-between">
                                <Text fontWeight="bold" fontSize="xl" color="blue.600">
                                  ${asset.price.toFixed(asset.price > 1 ? 2 : 6)}
                                </Text>
                                <Badge
                                  colorScheme={asset.priceChange24h >= 0 ? 'green' : 'red'}
                                  variant="subtle"
                                >
                                  {asset.priceChange24h >= 0 ? '+' : ''}{asset.priceChange24h.toFixed(2)}%
                                </Badge>
                              </HStack>
                              
                              <VStack align="stretch" spacing={2}>
                                <HStack justify="space-between">
                                  <Text fontSize="sm" color="gray.600">Market Cap</Text>
                                  <Text fontSize="sm" fontWeight="medium">
                                    ${(asset.marketCap / 1000000).toFixed(1)}M
                                  </Text>
                                </HStack>
                                
                                <HStack justify="space-between">
                                  <Text fontSize="sm" color="gray.600">24h Volume</Text>
                                  <Text fontSize="sm" fontWeight="medium">
                                    ${(asset.volume24h / 1000000).toFixed(1)}M
                                  </Text>
                                </HStack>
                              </VStack>

                              <Box>
                                <Text fontSize="xs" color="gray.500" mb={1}>Alpha Score</Text>
                                <Progress
                                  value={asset.alphaScore}
                                  colorScheme={
                                    asset.alphaScore >= 80 ? 'green' : 
                                    asset.alphaScore >= 60 ? 'yellow' : 'red'
                                  }
                                  size="sm"
                                  borderRadius="full"
                                />
                              </Box>

                              <HStack justify="space-between" pt={2}>
                                <Badge colorScheme="blue" variant="subtle">
                                  {asset.stage}
                                </Badge>
                                <Badge
                                  colorScheme={
                                    asset.riskLevel === 'LOW' ? 'green' :
                                    asset.riskLevel === 'MEDIUM' ? 'yellow' : 'red'
                                  }
                                  variant="outline"
                                  size="sm"
                                >
                                  {asset.riskLevel}
                                </Badge>
                              </HStack>
                            </VStack>
                          </CardBody>
                        </Card>
                      ))}
                    </SimpleGrid>
                  </Box>
                </VStack>
              </TabPanel>

              {/* Analytics Tab */}
              <TabPanel p={0} pt={6}>
                <AnalyticsDashboard />
              </TabPanel>

              {/* Portfolio Tab */}
              <TabPanel p={0} pt={6}>
                <PortfolioIntegration onWalletConnected={handleWalletConnected} />
              </TabPanel>

              {/* RWA Playbook Tab */}
              <TabPanel p={0} pt={6}>
                <RWAPlaybook autoRefresh={true} refreshInterval={120000} />
              </TabPanel>

              {/* Alerts Tab */}
              <TabPanel p={0} pt={6}>
                <VStack spacing={6} align="stretch">
                  <Box>
                    <Heading size="md" mb={4} color="gray.800">⚠️ Recent Alerts</Heading>
                    <TableContainer bg="white" borderRadius="lg" shadow="sm" border="1px solid" borderColor="gray.200">
                      <Table variant="simple">
                        <Thead bg="gray.50">
                          <Tr>
                            <Th>Priority</Th>
                            <Th>Alert</Th>
                            <Th>Message</Th>
                            <Th>Time</Th>
                            <Th>Status</Th>
                          </Tr>
                        </Thead>
                        <Tbody>
                          {alerts.map((alert, index) => (
                            <Tr key={index}>
                              <Td>
                                <Badge
                                  colorScheme={
                                    alert.priority === 'HIGH' ? 'red' :
                                    alert.priority === 'MEDIUM' ? 'yellow' : 'green'
                                  }
                                  variant="solid"
                                >
                                  {alert.priority}
                                </Badge>
                              </Td>
                              <Td fontWeight="medium">{alert.alert}</Td>
                              <Td color="gray.600">{alert.message}</Td>
                              <Td fontSize="sm" color="gray.500">{alert.time}</Td>
                              <Td>
                                <Badge
                                  colorScheme={alert.status === 'read' ? 'gray' : 'blue'}
                                  variant="subtle"
                                >
                                  {alert.status.toUpperCase()}
                                </Badge>
                              </Td>
                            </Tr>
                          ))}
                        </Tbody>
                      </Table>
                    </TableContainer>
                  </Box>
                </VStack>
              </TabPanel>

              {/* Projects Tab */}
              <TabPanel p={0} pt={6}>
                <VStack spacing={6} align="stretch">
                  <Box>
                    <Heading size="md" mb={4} color="gray.800">🚀 Discovery Projects</Heading>
                    <SimpleGrid columns={{ base: 1, lg: 3 }} spacing={6}>
                      {projects.map((project, index) => (
                        <Card key={index}>
                          <CardHeader>
                            <HStack justify="space-between">
                              <Text fontWeight="bold">{project.name}</Text>
                              <Badge
                                colorScheme={
                                  project.status === 'active' ? 'green' :
                                  project.status === 'paused' ? 'yellow' : 'gray'
                                }
                                variant="solid"
                              >
                                {project.status.toUpperCase()}
                              </Badge>
                            </HStack>
                          </CardHeader>
                          <CardBody pt={0}>
                            <VStack align="stretch" spacing={3}>
                              <Text fontSize="sm" color="gray.600">
                                {project.description}
                              </Text>
                              
                              <HStack justify="space-between">
                                <Text fontSize="sm" color="gray.500">Assets Tracked</Text>
                                <Text fontWeight="bold">{project.assetsTracked}</Text>
                              </HStack>
                              
                              <HStack justify="space-between">
                                <Text fontSize="sm" color="gray.500">Alpha Score</Text>
                                <Text fontWeight="bold">{project.alphaScore}%</Text>
                              </HStack>
                              
                              <Text fontSize="xs" color="gray.400">
                                Last alert: {project.lastAlert}
                              </Text>
                            </VStack>
                          </CardBody>
                        </Card>
                      ))}
                    </SimpleGrid>
                  </Box>
                </VStack>
              </TabPanel>

              {/* AlphaQuantBot Tab */}
              <TabPanel p={0} pt={6}>
                <AlphaQuantBotPanel />
              </TabPanel>
            </TabPanels>
          </Tabs>
        </VStack>
      </Container>
    </Box>
  );
}

export default App;